/**
 * PLACEHOLDER DATA — NOT REAL RECORDS.
 *
 * Human directive (2026-09-21, `agency-clients-listing-stage1.md` §"HUMAN DIRECTIVE") — the
 * reference design must be matched in full, including columns whose backing fields do not exist
 * yet. Originally client lead + next action + due date; **client lead is real now** (EW-FIX-77
 * T0248/T0250, item D1 — `RelatedOrg.clientLead`, wired in `ClientsPage.tsx`/`ClientsTable.tsx`;
 * `getPlaceholderLead`/`LEAD_NAMES`/`PLACEHOLDER_CURRENT_USER` are DELETED, not just unused — do
 * not re-add any of them or a client-side approximation). `nextAction` remains the placeholder
 * below (D2/E2, tracked separately, out of D1's scope) — the parallel BE slice was tracked in
 * `orchestrator/docs/orchestration/agency-surfaces-stage1/BE-REQUIREMENT-client-lead-next-action-portfolio.md`.
 *
 * **T0043 — serves BOTH persona branches of `/clients` (SA and agency), not agency alone.** The
 * human's directive was initially applied to the agency branch only; that was a too-narrow reading
 * — "all listing pages" means every render branch of a persona-split page, not the one branch a
 * PRD happened to name. `getPlaceholderNextAction` is keyed purely by `tenantId`, so it already
 * worked unmodified for SA rows once `ClientsTable`/`ClientTableRow` became the shared component
 * for both branches.
 *
 * Every value in this file is FE-side sample data, deterministically derived from a client's
 * `tenantId` so the same client always shows the same action across renders, sorts and re-fetches
 * (not random per render — a believable demo needs one, not a flicker). None of it is fetched,
 * none of it is real, and none of it should ever be read as a measured signal.
 *
 * **Removal path**: once the BE slice ships a real `nextAction` field (D2/E2), delete this file
 * and its remaining call sites. Nothing else in this workflow depends on it existing.
 *
 * **What this file deliberately does NOT do**: fabricate a number someone could act on as though
 * it were measured (an overdue count, a compliance score, a monetary total). It stubs only
 * structural/identifying values — a next-action label, a due date — per the directive's own line:
 * "do not fabricate a number that someone could act on as though it were measured... structural
 * and identifying placeholders are what this directive is for."
 */

/** BE field this placeholder stands in for: `Client.nextAction` (not in schema). */
export interface PlaceholderNextAction {
  label: string;
  /** ISO date, deterministically offset from a fixed reference so it's stable across renders. */
  dueDate: string;
  /** Derived from dueDate vs the fixed reference — used only to drive the "Needs attention" sample view. */
  isOverdueOrDueSoon: boolean;
}

// The anchor for this file's date math: the start of TODAY (UTC), taken once at module load. It is
// deliberately not `Date.now()` per call — that would make a due date drift while a page is open —
// and no longer a hardcoded calendar day, which had aged into the past ("3d overdue" beside a
// dashboard dated five days later). Stable within a session; each client keeps its offset.
export const PLACEHOLDER_REFERENCE_DATE = (() => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
})();

// Sourced from the guide's own text ("Plain actions such as Review ownership evidence, Follow up
// on signature, and Review activation"), not invented — plus one more in the same register for
// variety across the sample set.
const NEXT_ACTION_LABELS = [
  'Review ownership evidence',
  'Follow up on signature',
  'Review activation',
  'Confirm registered address',
] as const;

function hashToIndex(tenantId: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < tenantId.length; i++) {
    h = (h * 31 + tenantId.charCodeAt(i)) >>> 0;
  }
  return h % mod;
}

/** Deterministic placeholder next action + due date for a client. */
export function getPlaceholderNextAction(tenantId: string): PlaceholderNextAction {
  const labelIdx = hashToIndex(tenantId + ':label', NEXT_ACTION_LABELS.length);
  // Spread due dates across a window from 10 days overdue to 45 days out, deterministically.
  const offsetDays = (hashToIndex(tenantId + ':due', 56) - 10);
  const due = new Date(PLACEHOLDER_REFERENCE_DATE);
  due.setUTCDate(due.getUTCDate() + offsetDays);
  return {
    label: NEXT_ACTION_LABELS[labelIdx],
    dueDate: due.toISOString(),
    isOverdueOrDueSoon: offsetDays <= 7,
  };
}

export interface NextActionTiming {
  /** "Sep 17, 2026" — the explicit date. */
  dateLabel: string;
  /** "3d overdue" · "Due today" · "Due in 2d" · "" beyond a week. */
  timingLabel: string;
  tone: 'danger' | 'warning' | 'neutral';
}

/** The design's next-step sub-line ("Sep 17, 2026 · 3d overdue"), from the placeholder due date. */
export function describeNextActionTiming(
  dueDate: string,
  reference: Date = PLACEHOLDER_REFERENCE_DATE,
): NextActionTiming {
  const due = new Date(dueDate);
  const dateLabel = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const days = Math.round((due.getTime() - reference.getTime()) / 86_400_000);
  if (days < 0) return { dateLabel, timingLabel: `${-days}d overdue`, tone: 'danger' };
  if (days === 0) return { dateLabel, timingLabel: 'Due today', tone: 'warning' };
  if (days <= 7) return { dateLabel, timingLabel: `Due in ${days}d`, tone: 'warning' };
  return { dateLabel, timingLabel: '', tone: 'neutral' };
}
